import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { GelanggangDetail, Gelanggang, UserProfile } from '../../types';
import { getGelanggangDB, saveGelanggangDB, deleteGelanggangDB, getMembersDB } from '../../services/db';
import { getGoogleMapsUrl, formatPublicName, getGelanggangDisplayAddress, isSuperAdmin, hasRolePermission } from '../../utils/formatters';
import { processUploadedQrToSvg } from '../../utils/qrProcessor';
import { MapPin, Navigation, Plus, Edit2, Trash2, ExternalLink, Save, CheckCircle2, AlertCircle, ShieldAlert, Search, Phone, UserCheck, ChevronDown, Loader2, QrCode, CreditCard, Wallet, Upload, Check } from 'lucide-react';

interface GelanggangManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  initialGelanggangToEdit?: GelanggangDetail | null;
  isAddMode?: boolean;
}

export const GelanggangManagementModal: React.FC<GelanggangManagementModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  initialGelanggangToEdit = null,
  isAddMode = false
}) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const isPengerusi = currentUser?.role === 'Pengerusi' || isSuperAdmin(currentUser);
  const isKetuaGelanggang = currentUser?.role === 'Ketua Gelanggang';
  const canEdit = isPengerusi || isKetuaGelanggang;

  const [gelanggangs, setGelanggangs] = useState<GelanggangDetail[]>([]);
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [editingGelanggang, setEditingGelanggang] = useState<GelanggangDetail | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const loadData = () => {
    // 1. Read from localStorage cache instantly for a sub-second load
    const cachedG = localStorage.getItem('asmm_db_gelanggang_v5');
    const cachedM = localStorage.getItem('asmm_db_members_v4');
    let cacheGData: GelanggangDetail[] = [];
    let cacheMData: UserProfile[] = [];

    if (cachedG) {
      try {
        cacheGData = JSON.parse(cachedG);
        setGelanggangs(cacheGData);
      } catch {}
    }
    if (cachedM) {
      try {
        cacheMData = JSON.parse(cachedM);
        setMembers(cacheMData);
      } catch {}
    }

    // If cache is available and we are editing, initialize form instantly
    if (initialGelanggangToEdit) {
      const cachedRecord = cacheGData.find(g => g.id === initialGelanggangToEdit.id || g.name === initialGelanggangToEdit.name) || initialGelanggangToEdit;
      let phone = cachedRecord.leaderPhone || '';
      if (!phone && cachedRecord.leaderName && cacheMData.length > 0) {
        const match = cacheMData.find(m => m.name.toLowerCase().trim().includes(cachedRecord.leaderName.toLowerCase().trim()));
        if (match) phone = match.phone;
      }
      setEditingGelanggang({
        ...cachedRecord,
        address: cachedRecord.address || cachedRecord.location || '',
        gpsLocation: cachedRecord.gpsLocation || '',
        leaderPhone: phone
      });
      setMemberSearchQuery(cachedRecord.leaderName || '');
      setIsFormOpen(true);
      setIsLoading(false); // Hide spinner immediately
    }

    // 2. Fetch fresh records from Turso DB in background
    Promise.all([getGelanggangDB(), getMembersDB()]).then(([gData, mData]) => {
      setGelanggangs(gData);
      setMembers(mData);

      // Silently sync the form state with the latest server data in the background
      if (initialGelanggangToEdit) {
        const latest = gData.find(g => g.id === initialGelanggangToEdit.id || g.name === initialGelanggangToEdit.name) || initialGelanggangToEdit;
        let phone = latest.leaderPhone || '';
        if (!phone && latest.leaderName) {
          const match = mData.find(m => m.name.toLowerCase().trim().includes(latest.leaderName.toLowerCase().trim()));
          if (match) phone = match.phone;
        }

        setEditingGelanggang(prev => {
          if (!prev) return prev;
          // Only update if fields haven't changed during this session
          return {
            ...latest,
            address: prev.address === (cachedG ? (cacheGData.find(g => g.id === prev.id)?.address || '') : '') ? (latest.address || latest.location || '') : prev.address,
            gpsLocation: prev.gpsLocation === (cachedG ? (cacheGData.find(g => g.id === prev.id)?.gpsLocation || '') : '') ? (latest.gpsLocation || '') : prev.gpsLocation,
            leaderPhone: phone
          };
        });
      }
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (isOpen) {
      const cachedG = localStorage.getItem('asmm_db_gelanggang_v5');
      if (cachedG) {
        setIsLoading(false); // Skip loader since we have cache
      } else {
        setIsLoading(true); // First-time load shows spinner
      }

      loadData();

      if (initialGelanggangToEdit) {
        // Fallback instant initial state from props while cache load completes
        let phone = initialGelanggangToEdit.leaderPhone || '';
        setEditingGelanggang({
          ...initialGelanggangToEdit,
          address: initialGelanggangToEdit.address || initialGelanggangToEdit.location || '',
          gpsLocation: initialGelanggangToEdit.gpsLocation || '',
          leaderPhone: phone
        });
        setMemberSearchQuery(initialGelanggangToEdit.leaderName || '');
        setIsFormOpen(true);
      } else if (isAddMode) {
        setEditingGelanggang({
          id: `gel-custom-${Date.now()}`,
          name: 'Pusaka Warisan' as Gelanggang,
          location: '',
          address: '',
          gpsLocation: '',
          sessionTime: 'Jumaat & Sabtu (8.00 PM)',
          leaderName: '',
          leaderPhone: '',
          status: 'Aktif'
        });
        setMemberSearchQuery('');
        setIsFormOpen(true);
      } else {
        setIsFormOpen(false);
        setEditingGelanggang(null);
      }
    }
  }, [isOpen, initialGelanggangToEdit, isAddMode]);

  // Fuzzy filter members
  const filteredMembers = members.filter(m => {
    if (!memberSearchQuery.trim()) return true;
    const q = memberSearchQuery.toLowerCase().trim();
    return (
      m.name.toLowerCase().includes(q) ||
      m.role.toLowerCase().includes(q) ||
      m.gelanggang.toLowerCase().includes(q)
    );
  });

  const isGelanggangOwner = (g: GelanggangDetail) => {
    const isExec = Boolean(currentUser && hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Bendahari']));
    if (isExec) return true;
    if (!currentUser) return false;
    if (g.name === currentUser.gelanggang) return true;
    if (currentUser.name && g.leaderName && g.leaderName.toLowerCase().includes(currentUser.name.toLowerCase())) return true;
    if (currentUser.phone && g.leaderPhone && g.leaderPhone === currentUser.phone) return true;
    return false;
  };

  const handleOpenAddForm = () => {
    if (!isPengerusi) {
      showToast('error', 'Akses Terhad', 'Hanya Pengerusi dibenarkan menambah gelanggang baru.');
      return;
    }
    setEditingGelanggang({
      id: `gel-custom-${Date.now()}`,
      name: 'Pusaka Warisan' as Gelanggang,
      location: '',
      address: '',
      gpsLocation: '',
      sessionTime: 'Jumaat & Sabtu (8.00 PM)',
      leaderName: '',
      leaderPhone: '',
      status: 'Aktif'
    });
    setMemberSearchQuery('');
    setIsDropdownOpen(false);
    setIsFormOpen(true);
  };

  const handleOpenEditForm = (g: GelanggangDetail) => {
    if (!isGelanggangOwner(g)) {
      showToast('error', 'Akses Terhad', 'Ketua Gelanggang hanya dibenarkan mengemaskini gelanggang sendiri.');
      return;
    }
    // Auto-lookup leaderPhone from members table if missing
    let phone = g.leaderPhone || '';
    if (!phone && g.leaderName) {
      const match = members.find(m => m.name.toLowerCase().trim().includes(g.leaderName.toLowerCase().trim()));
      if (match) phone = match.phone;
    }
    setEditingGelanggang({
      ...g,
      address: g.address || g.location || '',
      gpsLocation: g.gpsLocation || '',
      leaderPhone: phone
    });
    setMemberSearchQuery(g.leaderName || '');
    setIsDropdownOpen(false);
    setIsFormOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGelanggang) return;

    if (!isPengerusi && !isGelanggangOwner(editingGelanggang)) {
      showToast('error', 'Akses Terhad', 'Anda hanya dibenarkan mengemaskini gelanggang sendiri.');
      return;
    }

    if (!editingGelanggang.name.trim()) {
      showToast('error', 'Sila masukkan nama gelanggang');
      return;
    }

    const addr = editingGelanggang.address?.trim() || editingGelanggang.location?.trim() || editingGelanggang.name.trim();

    try {
      const updated = await saveGelanggangDB({
        ...editingGelanggang,
        address: addr,
        location: addr,
        gpsLocation: editingGelanggang.gpsLocation?.trim() || ''
      });
      setGelanggangs(updated);
      showToast('success', 'Rekod Gelanggang Disimpan!', `Maklumat ${editingGelanggang.name} telah dikemaskini.`);
      setIsFormOpen(false);
      setEditingGelanggang(null);
      if (onSaved) onSaved();
      onClose();
    } catch {
      showToast('error', 'Ralat menyimpan gelanggang');
    }
  };

  const handleDelete = async (id: string) => {
    if (!isPengerusi) {
      showToast('error', 'Akses Terhad', 'Hanya Pengerusi dibenarkan memadam rekod gelanggang.');
      return;
    }
    try {
      const updated = await deleteGelanggangDB(id);
      setGelanggangs(updated);
      showToast('info', 'Gelanggang Dipadam', 'Rekod gelanggang telah dikeluarkan.');
      setConfirmDeleteId(null);
      if (onSaved) onSaved();
    } catch {
      showToast('error', 'Gagal memadam gelanggang');
    }
  };

  const handleCancel = () => {
    setIsFormOpen(false);
    setEditingGelanggang(null);
    onClose();
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingGelanggang?.id.includes('custom') ? "Tambah Gelanggang Baru" : "Kemaskini Gelanggang"}
      maxWidth="max-w-3xl"
    >
      <div className="space-y-6">
        {/* Helper Top Callout Banner */}
        <div className="bg-slate-900 text-white p-4 rounded-2xl border border-slate-800 flex items-start gap-3">
          <MapPin className="w-5 h-5 text-asmm-red shrink-0 mt-0.5" />
          <div className="text-xs space-y-1">
            <h4 className="font-extrabold text-white">Alamat & GPS Gelanggang Awam</h4>
            <p className="text-slate-300 leading-relaxed font-medium">
              Ketua Gelanggang & Pengerusi boleh menetapkan alamat penuh dan pautan GPS Google Maps untuk dipaparkan secara langsung di Laman Awam (Utama & Hubungi).
            </p>
          </div>
        </div>

        {!canEdit && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded-xl flex items-center gap-2.5 text-xs font-bold">
            <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0" />
            <span>Akses Terhad: Hanya Pengerusi dan Ketua Gelanggang sahaja yang dibenarkan mengemaskini butiran gelanggang.</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-3 bg-slate-50 border border-slate-200 rounded-2xl">
            <Loader2 className="w-8 h-8 animate-spin text-asmm-red" />
            <span className="text-xs text-slate-500 font-bold">Memuatkan maklumat...</span>
          </div>
        ) : editingGelanggang ? (
          <form onSubmit={handleSave} className="space-y-4 bg-slate-50 p-5 rounded-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h4 className="font-extrabold text-sm text-slate-900 flex items-center gap-2">
                <Edit2 className="w-4 h-4 text-asmm-red" />
                <span>{editingGelanggang.id.includes('custom') ? 'Tambah Gelanggang Baru' : `Kemaskini: ${editingGelanggang.name}`}</span>
              </h4>
              <button
                type="button"
                onClick={handleCancel}
                className="text-xs text-slate-500 hover:text-slate-900 font-bold"
              >
                Batal
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Nama Gelanggang */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Nama Gelanggang *</label>
                <input
                  type="text"
                  value={editingGelanggang.name}
                  onChange={(e) => setEditingGelanggang({ ...editingGelanggang, name: e.target.value as any })}
                  placeholder="Contoh: Pusaka Warisan / Gelanggang Paya Besar"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                  required
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Status Keaktifan *</label>
                <select
                  value={editingGelanggang.status}
                  onChange={(e) => setEditingGelanggang({ ...editingGelanggang, status: e.target.value as any })}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                >
                  <option value="Aktif">Aktif</option>
                  <option value="Tidak Aktif">Tidak Aktif</option>
                </select>
              </div>

              {/* Alamat */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1">Alamat *</label>
                <textarea
                  rows={2}
                  value={editingGelanggang.address || editingGelanggang.location || ''}
                  onChange={(e) => setEditingGelanggang({ ...editingGelanggang, address: e.target.value, location: e.target.value })}
                  placeholder="Contoh: Padang Awam Taman Sejahtera, Lunas, Kedah"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                  required
                />
              </div>

              {/* Pautan GPS Google Maps */}
              <div className="sm:col-span-2 space-y-1">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold text-slate-700">Pautan GPS Google Maps / Koordinat</label>
                  {editingGelanggang && (
                    <a
                      href={getGoogleMapsUrl(editingGelanggang)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-extrabold text-blue-600 hover:text-blue-800"
                    >
                      <span>Uji Pautan Maps</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <input
                  type="text"
                  value={editingGelanggang.gpsLocation || ''}
                  onChange={(e) => setEditingGelanggang({ ...editingGelanggang, gpsLocation: e.target.value })}
                  placeholder="Opsional: Masukkan URL Google Maps / Koordinat GPS (jika ada)"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-mono text-slate-900 focus:outline-none focus:border-asmm-red"
                />
                <p className="text-[11px] text-slate-500 italic">
                  * Jika dikosongkan, pautan navigasi Google Maps akan dijana secara automatik daripada nilai teks Alamat di atas.
                </p>
              </div>

              {/* Masa Sesi Latihan */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Masa Sesi Latihan *</label>
                <input
                  type="text"
                  value={editingGelanggang.sessionTime}
                  onChange={(e) => setEditingGelanggang({ ...editingGelanggang, sessionTime: e.target.value })}
                  placeholder="Contoh: Jumaat & Sabtu (8.00 PM)"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                  required
                />
              </div>

              {/* Ketua Gelanggang Fuzzy Combobox */}
              <div className="sm:col-span-2 relative space-y-1">
                <label className="block text-xs font-bold text-slate-700">
                  Nama Ketua Gelanggang *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                    <Search className="w-4 h-4 text-asmm-red" />
                  </div>
                  <input
                    type="text"
                    value={editingGelanggang.leaderName}
                    onFocus={() => setIsDropdownOpen(true)}
                    onChange={(e) => {
                      const val = e.target.value;
                      setMemberSearchQuery(val);
                      setIsDropdownOpen(true);
                      
                      // Auto-lookup matching member phone
                      const match = members.find(m => m.name.toLowerCase().trim().includes(val.toLowerCase().trim()));
                      setEditingGelanggang({
                        ...editingGelanggang,
                        leaderName: val,
                        leaderPhone: match ? match.phone : editingGelanggang.leaderPhone
                      });
                    }}
                    placeholder="Cari atau pilih nama Ketua Gelanggang..."
                    className="w-full pl-9 pr-8 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                  />
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                {/* Dropdown Suggestions List */}
                {isDropdownOpen && (
                  <div className="absolute z-30 left-0 right-0 mt-1 max-h-52 overflow-y-auto bg-white rounded-2xl border border-slate-200 shadow-xl py-1 divide-y divide-slate-100 animate-fade-in">
                    {filteredMembers.length > 0 ? (
                      filteredMembers.map(m => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => {
                            setEditingGelanggang({
                              ...editingGelanggang,
                              leaderName: m.name,
                              leaderPhone: m.phone || editingGelanggang.leaderPhone
                            });
                            setMemberSearchQuery(m.name);
                            setIsDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2.5 hover:bg-slate-50 text-xs font-semibold text-slate-900 transition-colors flex items-center justify-between"
                        >
                          <div className="space-y-0.5">
                            <div className="font-bold flex items-center gap-1.5">
                              <UserCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                              <span>{m.name}</span>
                            </div>
                            <div className="text-[10px] text-slate-500 font-medium">
                              Peranan: {m.role} • Gelanggang Asal: {m.gelanggang}
                            </div>
                          </div>
                          {m.phone && (
                            <span className="text-[10px] text-slate-500 font-mono font-bold bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                              {m.phone}
                            </span>
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-xs text-slate-500 italic text-center">
                        Tiada pesilat ditemui mengikut carian "{memberSearchQuery}"
                      </div>
                    )}
                  </div>
                )}

                {/* Clean Phone Display Subtext */}
                {editingGelanggang.leaderPhone && (
                  <div className="flex items-center gap-1.5 pt-1 text-xs text-slate-600 font-medium">
                    <Phone className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>No. Telefon: <strong className="font-mono text-slate-900">{editingGelanggang.leaderPhone}</strong></span>
                  </div>
                )}
              </div>
            </div>

            {/* Saluran Pembayaran Yuran (FPX Persatuan vs QR Sendiri Ketua Gelanggang) */}
            <div className="pt-3 border-t border-slate-200 space-y-3">
              <label className="block text-xs font-bold text-slate-700 flex items-center justify-between">
                <span>Saluran Pembayaran Yuran Gelanggang</span>
                <span className="text-[10px] font-mono font-bold text-slate-500">
                  {editingGelanggang.paymentPreference === 'qr_ketua' ? 'QR Ketua Gelanggang' : 'FPX Bank Persatuan'}
                </span>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setEditingGelanggang({ ...editingGelanggang, paymentPreference: 'fpx' })}
                  className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer flex items-center gap-2.5 ${
                    (editingGelanggang.paymentPreference || 'fpx') === 'fpx'
                      ? 'bg-emerald-50/50 border-emerald-500 text-slate-900 shadow-xs'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <CreditCard className="w-4 h-4 text-emerald-600 shrink-0" />
                  <div className="min-w-0">
                    <span className="text-xs font-extrabold block truncate">FPX Bank Persatuan</span>
                    <span className="text-[10px] text-slate-500 block truncate">ToyyibPay Auto (Muamalat)</span>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setEditingGelanggang({ ...editingGelanggang, paymentPreference: 'qr_ketua' })}
                  className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer flex items-center gap-2.5 ${
                    editingGelanggang.paymentPreference === 'qr_ketua'
                      ? 'bg-amber-50/60 border-amber-500 text-slate-900 shadow-xs'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <QrCode className="w-4 h-4 text-amber-600 shrink-0" />
                  <div className="min-w-0">
                    <span className="text-xs font-extrabold block truncate">QR Sendiri Ketua</span>
                    <span className="text-[10px] text-slate-500 block truncate">DuitNow QR + Muat Naik Resit</span>
                  </div>
                </button>
              </div>

              {editingGelanggang.paymentPreference === 'qr_ketua' && (
                <div className="p-3.5 bg-amber-50/40 rounded-xl border border-amber-200 space-y-3 animate-fade-in">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 mb-1">Nama Bank</label>
                      <input
                        type="text"
                        value={editingGelanggang.qrBankName || ''}
                        onChange={(e) => setEditingGelanggang({ ...editingGelanggang, qrBankName: e.target.value })}
                        placeholder="cth: Maybank / CIMB"
                        className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 mb-1">Nama Pemegang Akaun</label>
                      <input
                        type="text"
                        value={editingGelanggang.qrAccountName || ''}
                        onChange={(e) => setEditingGelanggang({ ...editingGelanggang, qrAccountName: e.target.value })}
                        placeholder="cth: Mohd Fauzi"
                        className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 mb-1">Nombor Akaun Bank</label>
                      <input
                        type="text"
                        value={editingGelanggang.qrAccountNo || ''}
                        onChange={(e) => setEditingGelanggang({ ...editingGelanggang, qrAccountNo: e.target.value })}
                        placeholder="cth: 152012345678"
                        className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-bold font-mono text-slate-900 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 mb-1">Muat Naik Gambar QR</label>
                      <label className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-700 cursor-pointer hover:border-amber-500 truncate">
                        <Upload className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <span className="truncate">{editingGelanggang.qrImageUrl ? 'Ganti QR' : 'Pilih Gambar QR'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const processed = await processUploadedQrToSvg(file);
                              if (processed) {
                                setEditingGelanggang({ ...editingGelanggang, qrImageUrl: processed.svgDataUrl });
                                showToast('success', 'Kod QR Ditukar ke Digital SVG (1:1)', 'Imej kod QR berjaya diimbas dan dijana ke format 1:1 SVG digital.');
                                return;
                              }
                            } catch (err) {}
                            const reader = new FileReader();
                            reader.onload = () => {
                              setEditingGelanggang({ ...editingGelanggang, qrImageUrl: reader.result as string });
                            };
                            reader.readAsDataURL(file);
                          }}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Form Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold rounded-xl transition-all"
              >
                Batal
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-extrabold rounded-xl transition-all shadow-md cursor-pointer"
              >
                <Save className="w-4 h-4" />
                <span>Simpan Maklumat</span>
              </button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 space-y-3 bg-slate-50 border border-slate-200 rounded-2xl">
            <Loader2 className="w-8 h-8 animate-spin text-asmm-red" />
            <span className="text-xs text-slate-500 font-bold">Menyediakan borang kemaskini...</span>
          </div>
        )}
      </div>
    </Modal>
  );
};
